import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, } from "@angular/material/dialog";
import { WatchlistService } from "@app2/alert/watchlist.service";
import { Watchlist } from "@app2/type-defs/search/search-types";
import { SearchClientService } from "@app2/clients/search-client.service";
import { localization } from "@app2/shared/localization/localization";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

interface DialogData {
    indicator: string;
}

@UntilDestroy()
@Component({
    selector: "add-indicator-modal",
    template: `
        <span mat-dialog-title>
            {{ "ADD_TO_WATCHLIST" | localize }}
        </span>
        <mat-dialog-content>
            <article>
                <user-banner *ngIf="addError"
                             type="alert"
                             [topText]="addError.topText"
                             [bottomText]="addError.bottomText">
                </user-banner>
                <div class="nice-form">
                    <label>
                        <span class="required-asterisk">*</span> {{ "WATCHLIST" | localize }}
                    </label>
                    <ds-dropdown [showFooter]="false" test-id="watchlist-dropdown">
                        <div id="ds-dropdown-trigger" class="dropdown-value">
                            {{ selectedWatchlist?.name }}
                        </div>
                        <div id="ds-dropdown-header-row">
                            <search-box placeholderKey="SEARCH_WATCHLIST"
                                        autocomplete="off"
                                        (searchTextChange)="updateFilter($event)">
                            </search-box>
                        </div>
                        <li *ngFor="let watchlist of filteredWatchlists"
                            class="watchlist-option"
                            (click)="this.selectWatchlist(watchlist)"
                            pr-dropdown-close-on-click>
                            <span class="community-watchlist-icon">
                                <svg-icon *ngIf="watchlist.communityWatchlist"
                                          [inline]="true"
                                          src="assets/icons/ds-label.svg">
                                </svg-icon>
                            </span>
                            {{ watchlist.name }}
                        </li>
                    </ds-dropdown>

                    <add-indicators-reason *ngIf="selectedWatchlist"
                                           [communityWatchlist]="selectedWatchlist.communityWatchlist"
                                           [type]="selectedWatchlist.type"
                                           [(reason)]="reason">
                    </add-indicators-reason>
                </div>
            </article>
        </mat-dialog-content>
        <mat-dialog-actions>
            <modal-buttons saveKey="ADD"
                           [saveInProgress]="saving"
                           [canSave]="selectedWatchlist !== null"
                           (onCancel)="onCancel()"
                           (onSave)="onSave()">
            </modal-buttons>
        </mat-dialog-actions>
    `,
    styles: [`
        .watchlist-option {
            display: flex;
            align-items: center;
            padding-top: 5px;
            padding-bottom: 5px;
        }

        .community-watchlist-icon {
            display: inline-block;
            height: 16px;
            width: 16px;
            margin-right: 4px;
        }
    `]
})
export class AddIndicatorModalComponent {
    watchlists: Watchlist[];
    filteredWatchlists: Watchlist[];
    selectedWatchlist: Watchlist = null;
    filter = "";
    reason = "";
    addError: { topText: string, bottomText: string };
    saving = false;

    console = console;

    constructor(private readonly dialogRef: MatDialogRef<AddIndicatorModalComponent>,
                private readonly watchlistService: WatchlistService,
                private readonly searchClientService: SearchClientService,
                private readonly notificationsService: NotificationsService,
                @Inject(MAT_DIALOG_DATA) public data: DialogData) {
        this.watchlistService.getWatchlists$()
            .pipe(untilDestroyed(this))
            .subscribe(watchlists => {
                this.watchlists = watchlists;
                this.filterWatchlists();
            });
    }

    updateFilter(value: string) {
        this.filter = value.toLowerCase();
        this.filterWatchlists();
    }

    filterWatchlists() {
        this.filteredWatchlists = this.watchlists.filter(list => list.name.toLowerCase().includes(this.filter));
    }

    selectWatchlist(watchlist: Watchlist) {
        this.selectedWatchlist = watchlist;
        this.reason = "";
    }

    onSave(): void {
        this.saving = true;

        this.searchClientService.editWatchlistItems(this.selectedWatchlist.id, {
            items: [{ id: null, value: this.data.indicator, reason: this.reason, enabled: true }],
        }, this.reason, false)
            .then(result => {
                // assuming we only add one item so either one is the cause
                if (result.invalidIndicators.length > 0) {
                    this.addError = {
                        topText: localization.getString("FAILED_TO_ADD_TO_WATCHLIST"),
                        bottomText: localization.getString("INDICATOR_WAS_INVALID_MESSAGE",
                            this.data.indicator,
                            this.selectedWatchlist.name,
                            this.selectedWatchlist.type),
                    };
                } else if (result.duplicateIndicators.length > 0) {
                    this.addError = {
                        topText: localization.getString("FAILED_TO_ADD_TO_WATCHLIST"),
                        bottomText: localization.getString("INDICATOR_WAS_A_DUPLICATE_MESSAGE",
                            this.data.indicator,
                            this.selectedWatchlist.name),
                    };
                } else {
                    this.notificationsService.showSuccess("UPDATE_INDICATORS_SUCCESS");
                    this.watchlistService.reloadWatchlistItemsForWatchlist(this.selectedWatchlist.id);
                    this.dialogRef.close(this.selectedWatchlist.id);
                }
            })
            .catch(error => {
                const message = getMessageForError(error.error, "ERROR_UPDATING_WATCHLIST_ITEMS");
                this.notificationsService.showError(message, error);
            })
            .finally(() => this.saving = false);
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}